import React from 'react';
import { Button } from '../../Utility/Button';
import Input from '../../Utility/FormElements/Input';
import AdminContainer from '../../Utility/AdminContainer';
import AdminBox from '../../Utility/AdminBox';
import AdminBoxHeadline from '../../Utility/AdminBoxHeadline';
import TextareaAutosize from 'react-textarea-autosize';
import useKnowledgebase from '../../../Hooks/useKnowledgebase';
import { useParams } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { Warning, X } from '@phosphor-icons/react';
import { Spinner } from '../../Utility/Spinner';
import { Badge } from '../../Utility/Badge';
import PaginatedFileList from "./components/PaginatedFileList";
import {PaginationListType} from '../../../Types/PaginationParams';
import {Document} from "../../../Types";
import FileListItem from "./components/FileListItem";

const Knowledgebase: React.FC = () => {
    const { id } = useParams();
    const {
        isEditMode,
        isLoaded,
        isSaving,
        formData,
        i18n,
        handleSubmit,
        handleChange,
        handleDocumentUpload,
        handleReembedDocument,
        handleRemoveExistingDocument,
        knowledgebaseSnapshot,
        fileDuplicates,
        setFileDuplicates,
        getKnowledgebaseDocuments,
    } = useKnowledgebase();

    const onDrop = (acceptedFiles: File[]) => {
        setFileDuplicates({});
        handleDocumentUpload(acceptedFiles);
    };

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
    });

    const removeDocument = (index: number) => {
        const newDocuments = [...formData.documents];
        newDocuments.splice(index, 1);
        handleChange('documents', newDocuments as any);
    };

    return (
        <div className="admin-container flex relative flex-col">
            <AdminContainer>
                <AdminBox contentLoaded={isLoaded}>
                    <div className="flex flex-col gap-5">
                        <AdminBoxHeadline
                            headLine={
                                isEditMode
                                    ? i18n.translate({ id: 'title.form.edit', sourceName: 'Knowledgebase' })
                                    : i18n.translate({ id: 'title.form.create', sourceName: 'Knowledgebase' })
                            }
                            subLine={id !== 'new' ? knowledgebaseSnapshot?.name : undefined}
                        />

                        <label htmlFor="name" className="flex flex-col gap-2">
                            {i18n.translate({ id: 'form.name', sourceName: 'Knowledgebase' })}
                            <Input
                                inputType="text"
                                value={formData.name}
                                onChange={(value) => handleChange('name', value)}
                                required
                                id="name"
                            />
                        </label>

                        <label htmlFor="description" className="flex flex-col gap-2">
                            {i18n.translate({ id: 'form.description', sourceName: 'Knowledgebase' })}
                            <TextareaAutosize
                                id="description"
                                className="bg-input-backgroundColor w-full p-3 border border-2 border-input-borderColor rounded-lg outline-none focus:border-primary transition-all duration-100"
                                value={formData.description}
                                onChange={(e) => handleChange('description', e.target.value)}
                                minRows={4}
                                maxRows={8}
                            />
                        </label>

                        <div className="flex flex-col gap-4">
                            <div
                                {...getRootProps()}
                                className={`mt-4 border-2 border-dashed border-input-borderColor rounded-lg p-6 text-center cursor-pointer ${
                                    isDragActive ? 'border-primary' : 'hover:border-primary'
                                } transition-all duration-100`}
                            >
                                <input {...getInputProps()} />
                                <div>
                                    <p className="mb-2">
                                        {i18n.translate({ id: 'documents.drag', sourceName: 'Knowledgebase' })}
                                    </p>
                                    <Button>
                                        {i18n.translate({ id: 'button.upload', sourceName: 'Knowledgebase' })}
                                    </Button>
                                </div>
                            </div>

                            {Object.keys(fileDuplicates).length > 0 && (
                                <Badge variant="info" className="w-full mt-2">
                                    <div className="flex items-start">
                                        <div className="flex-shrink-0">
                                            <Warning size={20} />
                                        </div>
                                        <div className="ml-2">
                                            <div className="text-sm font-bold">
                                                {i18n.translate({ id: 'duplicate.title', sourceName: 'FileDlpModal' })}
                                            </div>
                                            <div className="text-xs whitespace-pre-line">
                                                {i18n.translate({
                                                    id: 'duplicate.description',
                                                    sourceName: 'FileDlpModal',
                                                })}
                                                <ul className="py-0">
                                                    {Object.values(fileDuplicates)
                                                        .map((files) =>
                                                            files.slice(1).map((file) => (
                                                                <li key={file.name} className="my-1">
                                                                    {file.name}
                                                                </li>
                                                            )),
                                                        )
                                                        .flat()}
                                                </ul>
                                            </div>
                                        </div>
                                    </div>
                                </Badge>
                            )}
                            <PaginatedFileList<Document|{ name: string, sha1: string }> title={i18n.translate({ id: 'list.selectedDocuments', sourceName: 'Knowledgebase' })} isNew={!(id && id !== 'new')} selectedDocumentCount={formData.documents.length} listType={PaginationListType.KnowledgebaseExistingFiles} renderItem={(document, index: number) => {
                                if((document as Document).identifier){
                                    return <FileListItem key={(document as Document).identifier} formData={formData} document={document as Document} handleReembedDocument={handleReembedDocument} handleRemoveExistingDocument={handleRemoveExistingDocument}/>
                                }
                                const file = document as { name: string, sha1: string }
                                return (
                                    <div
                                        key={`new-${index}`}
                                        className="flex my-2 items-center justify-between bg-input-backgroundColor rounded-lg px-4 py-3 w-full"
                                    >
                                        <span className="text-sm flex-grow">{file.name}</span>
                                        <div className="flex items-center gap-3">
                                            <button
                                                onClick={() => removeDocument(index)}
                                                className="text-gray-500 hover:text-gray-700"
                                                title={i18n.translate({
                                                    id: 'delete.button',
                                                    sourceName: 'Knowledgebase',
                                                })}
                                            >
                                                <X size={16} />
                                            </button>
                                        </div>
                                    </div>
                                )
                            }} itemsFunction={getKnowledgebaseDocuments}/>
                        </div>
                        <div className="mt-5 text-right">
                            <Button
                                variant={isSaving ? 'secondary' : 'primary'}
                                onClick={handleSubmit}
                                disabled={isSaving}
                            >
                                {isSaving ? (
                                    <div className="flex items-center gap-2">
                                        <Spinner size="sm" />
                                        {i18n.translate({ id: 'saving', sourceName: 'Knowledgebase' })}
                                    </div>
                                ) : isEditMode ? (
                                    i18n.translate({ id: 'save', sourceName: 'Main' })
                                ) : (
                                    i18n.translate({ id: 'form.create', sourceName: 'Knowledgebase' })
                                )}
                            </Button>
                        </div>
                    </div>
                </AdminBox>
            </AdminContainer>
        </div>
    );
};

export default Knowledgebase;
